import { useState, useEffect } from 'react';
import { listOrgMembers, inviteMember, removeMember } from '../api';

export default function OrgMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    try {
      const data = await listOrgMembers();
      setMembers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setError(''); setInviting(true);
    try {
      await inviteMember({ email: inviteEmail, role: 'admin' });
      setSuccess(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInvite(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally { setInviting(false); }
  };

  const handleRemove = async (userId, email) => {
    if (!window.confirm(`Remove ${email || userId} from this organization?`)) return;
    try {
      await removeMember(userId);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const roleBadge = (role) => {
    const styles = {
      owner: { background: 'rgba(59,130,246,0.15)', color: '#60a5fa' },
      admin: { background: 'rgba(16,185,129,0.15)', color: '#34d399' },
    };
    const s = styles[role] || { background: 'var(--surface-2)', color: 'var(--muted)' };
    return (
      <span style={{ ...s, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
        {role}
      </span>
    );
  };

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="page-header">
        <div>
          <h2>Team Members</h2>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            Manage who has access to your organization
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          + Invite Member
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Invite a Co-Manager</div>
          <form onSubmit={handleInvite}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="email" value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  required autoFocus placeholder="comanager@example.com"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: 14, color: 'var(--text)', background: 'var(--surface-2)' }}
                />
                <div className="helper-text" style={{ marginTop: 5 }}>
                  They'll receive an email with a link to join your organization as an Admin.
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={inviting}>
                {inviting ? 'Sending…' : 'Send Invite'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members table */}
      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Loading…</p>
      ) : members.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <p style={{ color: 'var(--muted)' }}>No team members yet. Invite someone to get started.</p>
        </div>
      ) : (
        <table className="list-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.userId}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: m.role === 'owner' ? 'var(--navy)' : 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: m.role === 'owner' ? 'white' : 'var(--muted)',
                      flexShrink: 0,
                    }}>
                      {(m.email || m.userId || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{m.email || m.userId}</div>
                    </div>
                  </div>
                </td>
                <td>{roleBadge(m.role)}</td>
                <td style={{ color: 'var(--muted)', fontSize: 13 }}>
                  {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </td>
                <td>
                  {m.role !== 'owner' ? (
                    <button
                      className="btn btn-xs"
                      style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--red)' }}
                      onClick={() => handleRemove(m.userId, m.email)}>
                      Remove
                    </button>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>Owner</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
